import React, { useState, useEffect } from "react";
import Select from 'react-select';
import { withI18n } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
// @ts-ignore
import { CWBModal } from "cwb-react";
import { toast } from "react-toastify";
import { Button, ButtonGroup } from "../Common";
import { iRootState } from "../../store";
import { IResendStats } from "../../shared/dto/IKioskDto";
import CWBRTheme from "../../Theme/CWBRSTheme";

interface Props extends StateProps, DispatchProps {
  t?: any;
}

const ResendAuditionLinkModal = (props: Props) => {
  const numActorsTotal = props.resendStats && props.resendStats.allActorsQty;
  const [selectedNames, setNames] = useState([]);
  
  const { t } = props;
  const linkSelectorOptions = [
    {label: `${t('Changes to Schedule')}`, value: 1},
    {label: `${t('All actors on the schedule')} ${numActorsTotal}`, value: 2},
    {label: `${t('Select individual actors')}`, value: 3}
  ];
  const [selected, setSelected] = useState(linkSelectorOptions[0]);

  const numActorsNew = props.resendStats && props.resendStats.newActorsQty;
  const { showResendLinkModal, getResendStats, setResendStats } = props;

  const listOfNames = props.auditionsDetails.reduce((arr :any, actorInfo :any )=> {
    interface Actor {
      label: string;
      value: number;
    }
    let checkValue: Actor = {label: actorInfo.actor, value: actorInfo.clientId}
    let found = arr.some((name :any) => name.label === actorInfo.actor)

    if (!found) arr.push(checkValue);
    return arr;
  }, [])

  const checkWhichSelected = (value: any) => {
    let selectedValue = value.value;
    if(selectedValue !== 3) {
      setNames([])
    }
    setSelected(linkSelectorOptions[selectedValue - 1])
  }

  const addToList = (value: any) => {
    setNames(value.map((x :{ label: string; value: number }) => x.value));
  }
  
  useEffect(() => {
    getResendStats(showResendLinkModal.scheduleId);
    return () => setResendStats({} as IResendStats);
  }, [showResendLinkModal, getResendStats, setResendStats]);

  const resend = async () => {
    const success = await props.resendAuditionLink({
      scheduleId: props.showResendLinkModal.scheduleId,
      allActors: selected.value === 1,
      newActors: selected.value === 2,
      targetIds: selectedNames
    });
    if (success) {
      toast.success(t("Virtual Audition link resent!"), {
        position: "top-right"
      });
    } else if (success === false) {
      toast.error((
        <span>
          {t("Some attendees did not receive the Virtual Audition Link.")}
          &nbsp;
          <StyledButton
            onClick={() => props.setShowFailedEmailModal(true)}
          >
            {t("view details")}
          </StyledButton>
        </span>
      ), {
        position: "top-right",
        autoClose: false
      });
    }
    props.setShowResendLinkModal(false);
  };

  let baseUrl = process.env.PUBLIC_URL ? process.env.PUBLIC_URL : "";

  return (
    <StyledModal
      className="cwb-modal"
      isOpen={true}
      hideModal={() => props.setShowResendLinkModal(false)}
      buttons={
        <StyledButtonGroup>
          <Button
            disabled={selectedNames.length === 0 && selected.value === 3}
            onClick={() => resend()}
          >
            <StyledSpan>
              {t("Send")}
              {props.loading && (
                <Spinner src={`${baseUrl}/images/spinner.svg`} />
              )}
            </StyledSpan>
          </Button>
          <Button
            variant="secondary"
            onClick={() => props.setShowResendLinkModal(false)}
          >
            {t("Cancel")}
          </Button>
        </StyledButtonGroup>
      }
    >
      <Title>{t("Resend Audition Link")}</Title>
      <Text>
        {t("The Virtual Audition link and all related materials will be sent to the following")}:
      </Text>
      <Text>
        {t("Resend link to")}:
      </Text>
        <div>
          <Select
            options={linkSelectorOptions}
            placeholder="Changes to the schedule"
            onChange={checkWhichSelected}
            styles={selectStyle}
            value={selected}
          />
        </div>
        <div>
          {selected.value === 3 ?
          <div>
            <Text>
              {t("Recipient(s)")}
            </Text>
            <Select 
              options={ listOfNames } 
              placeholder= "Select one or mulitple" 
              onChange={addToList}
              styles={selectStyle}
              isMulti
              autoFocus 
            /> 
          </div> 
          :
          <div>
            {t("All actors on the schedule")}: <span>{numActorsTotal}</span>
            <br/>
            {t("New actors added to the schedule")}: <span>{numActorsNew}</span>
          </div>
          
          }
        </div>
    </StyledModal>
  );
};

const StyledModal = styled(CWBModal)`
  && {
    width: 600px;
    max-width: unset;
  }

  * { text-align: left; }
`;

const Title = styled.h1`
  color: #0A1F44;
  font-size: ${p => p.theme["xxl-font-size"]};
  font-weight: bold;
`;

const Text = styled.p`
  color: #53627C;
  font-size: 14px;
`;

const selectStyle = {
  control: (styles :any) => ({ ...styles, marginBottom: '1rem' }),
  multiValue: (styles :any) => {
    return {
      ...styles,
      backgroundColor: '#F8F9FB',
      border: '1px solid #C9CED6'
    };
  },
  multiValueRemove: (styles :any) => ({
    ...styles,
    color: 'C9CED6',
    'hover': {
      backgroundColor: CWBRTheme.red
    },
  }),
};

const StyledButtonGroup = styled(ButtonGroup)`
  width: 100%;
`;

const StyledSpan = styled.span`
  display: flex;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  & > img { margin-left: 4px; }
`;

const Spinner = styled.img`
  width: 16px;
`;

const StyledButton = styled.button`
  color: #0F9BF0;
  padding: 0;
  background-color: #FFF;
  border: none;
  text-decoration: underline;
`;

interface StateProps {
  loading: boolean;
  showResendLinkModal: { show: boolean, scheduleId: number };
  resendStats: IResendStats;
  auditionsDetails: any;
}

const mapStateToProps = (state: iRootState) => {
  return ({loading: state.kiosk.loading,
  auditionsDetails: state.kiosk.sessionAuditions.auditionsDetails,
  showResendLinkModal: state.kiosk.showResendLinkModal,
  resendStats: state.kiosk.resendStats})
};

interface DispatchProps {
  setShowFailedEmailModal: (show: boolean) => any;
  setShowResendLinkModal: (show: boolean, scheduleId?: number) => any;
  getResendStats: (scheduleId: number) => void;
  setResendStats: (resendStats: IResendStats) => any;
  resendAuditionLink: (resendDetails: any) => boolean;
}

const mapDispatchToProps = (dispatch: any) => ({
  setShowFailedEmailModal: dispatch.kiosk.setShowFailedEmailModal,
  setShowResendLinkModal: dispatch.kiosk.setShowResendLinkModal,
  getResendStats: dispatch.kiosk.getResendStats,
  setResendStats: dispatch.kiosk.setResendStats,
  resendAuditionLink: dispatch.kiosk.resendAuditionLink
});

export default withI18n()(connect(
  mapStateToProps,
  mapDispatchToProps
)(ResendAuditionLinkModal));